import axios from 'axios'
import { apiGet, apiPost } from './api'

const BASE = (import.meta.env.VITE_APP_API_GATEWAY_URL as string) ?? ''
const PREFIX = '/awgment-llm-local'
const TOKEN_KEY = 'access_token'

function authHeaders() {
  return { Authorization: `Bearer ${sessionStorage.getItem(TOKEN_KEY)}` }
}

// ─── Chat ─────────────────────────────────────────────────────────────────────

export const chatVectorDb = (userInput: string, projectName: string) =>
  apiPost(`${PREFIX}/chat/vector_db`, { user_input: userInput, project_name: projectName })

/** Chat assistant for Blueprint phase */
export const chatBlueprintApi = (payload: {
  user_input: string
  blueprint?: string
  file_name?: string
}) =>
  apiPost(`${PREFIX}/chat/blueprint`, {
    user_input: payload.user_input,
    ...(payload.blueprint !== undefined && { blueprint: payload.blueprint }),
    ...(payload.file_name !== undefined && { file_name: payload.file_name }),
  })

/** Chat assistant for Build phase */
export const chatBuildApi = (payload: {
  user_input: string
  source_code?: string
  transformed_code?: string
  source_lang?: string
  target_lang?: string
}) =>
  apiPost(`${PREFIX}/chat/build`, {
    user_input: payload.user_input,
    ...(payload.source_code !== undefined && { source_code: payload.source_code }),
    ...(payload.transformed_code !== undefined && { transformed_code: payload.transformed_code }),
    ...(payload.source_lang !== undefined && { source_lang: payload.source_lang }),
    ...(payload.target_lang !== undefined && { target_lang: payload.target_lang }),
  })

/** Chat assistant for Validation phase */
export const chatValidationApi = (payload: {
  user_input: string
  test_code?: string
  test_results?: Array<{ name: string; status: string; duration?: string }>
}) =>
  apiPost(`${PREFIX}/chat/validation`, {
    user_input: payload.user_input,
    ...(payload.test_code !== undefined && { test_code: payload.test_code }),
    ...(payload.test_results !== undefined && { test_results: payload.test_results }),
  })

// ─── Upload ───────────────────────────────────────────────────────────────────

// asset_type values accepted by the backend (uppercase)
export type UploadAssetType = 'QVS' | 'SAS' | 'JAVA' | 'GENERAL'

/**
 * Upload one or more source files to the knowledge base.
 * POST /awgment-llm-local/upload — multipart/form-data
 * Field name must be "files" (plural). asset_type must be uppercase.
 */
export const uploadKnowledgeAsset = async (params: {
  files: File[]
  projectName: string
  assetType: UploadAssetType
  assetName?: string
}): Promise<{ success: boolean; data?: any; message?: string }> => {
  try {
    const formData = new FormData()
    params.files.forEach((f) => formData.append('files', f))
    formData.append('project_name', params.projectName)
    formData.append('asset_type', "QVS")
    if (params.assetName) formData.append('asset_name', params.assetName)
    const response = await axios.post(`${BASE}${PREFIX}/upload`, formData, {
      headers: { ...authHeaders() },
    })
    return { success: true, data: response.data }
  } catch (error: unknown) {
    return {
      success: false,
      message:
        (error as any)?.response?.data?.detail ??
        (error as any)?.response?.data?.message ??
        (error as any)?.message ??
        'Upload failed',
    }
  }
}

// ─── Asset lists ─────────────────────────────────────────────────────────────

/**
 * Returns all uploaded/processed knowledge assets for the current user.
 * Filter client-side by project_name to get files for a specific project.
 * Shape: { mongo_knowledge_assets: KnowledgeAsset[] }
 */
export const getKnowledgeAssetsUser = () =>
  apiGet(`${PREFIX}/knowledge_assets_user`)

/**
 * Returns all UPC/DBML processed assets (pseudocode, target_code, test_cases).
 * Filter client-side by project_name to derive per-file phase progress.
 * Shape: { mongo_knowledge_assets: UpcAsset[] }
 */
export const getUpcDbmlAssets = (projectId?: string, phase?: string) =>
  apiGet(`${PREFIX}/upc_dbml_assets_user`, {
    ...(projectId ? { project_id: projectId } : {}),
    ...(phase ? { phase } : {}),
  })

export interface KnowledgeAsset {
  asset_id: string
  asset_name: string
  project_name: string
  type: string
  db_type?: string
  status?: string
  creation_date?: string
  documentName?: string
  /** Filename with extension (e.g. "file.qvs") */
  file_name?: string
  /** For non-ZIP assets extracted from a ZIP: the parent ZIP's asset_id */
  parent_asset_id?: string
  /** True when this asset was uploaded as a ZIP file */
  is_zip?: boolean
  /** Nested folder/file tree for ZIP assets.
   *  Backend format: { name, type:"folder"|"file", asset_id?, children? } */
  tree?: Record<string, any> | Array<any>
}

/**
 * Trigger KA (Knowledge Asset) generation for the given asset IDs.
 * Always pass child asset_ids (never parent ZIP id) when dealing with ZIP uploads.
 */
export const generateKaApi = (assetIds: string[], assetType = 'QVS') =>
  apiPost(`${PREFIX}/generate_ka`, { asset_ids: assetIds, asset_type: assetType })

// ─── SSE types ───────────────────────────────────────────────────────────────

export interface KaStageEvent {
  asset_id: string
  stage: string
  message: string
  progress: number
  ka_id?: string
}

export interface KaFileDoneEvent {
  status: string
  file_name: string
  processed_files: Array<{
    audience: string
    asset_id: string
    ka_id: string
    file_id: string
    project_id: string
  }>
}

export interface KaSummaryEvent {
  status: string
  processed: Array<{
    audience: string
    asset_id: string
    ka_id: string
    file_id: string
    project_id: string
    file_name: string
  }>
  failed: Array<unknown>
}

/**
 * SSE variant of KA generation. Streams progress events from the same endpoint.
 * Returns a cancel function — call it to abort the request early.
 *
 * Events received:
 *   stage      → onStage(data)   — progress updates with stage name + message
 *   file_done  → onFileDone(data)
 *   summary    → onSummary(data)
 *   keepalive  → ignored
 */
export function generateKaApiSSE(
  assetIds: string[],
  assetType = 'QVS',
  callbacks: {
    onStage?: (data: KaStageEvent) => void
    onFileDone?: (data: KaFileDoneEvent) => void
    onSummary?: (data: KaSummaryEvent) => void
    onError?: (err: string) => void
    onDone?: () => void
  },
): () => void {
  const controller = new AbortController()

    ; (async () => {
      try {
        const response = await fetch(`${BASE}${PREFIX}/generate_ka`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...authHeaders(),
          },
          body: JSON.stringify({ asset_ids: assetIds, asset_type: assetType }),
          signal: controller.signal,
        })

        if (!response.ok) {
          const text = await response.text().catch(() => '')
          callbacks.onError?.(text || `HTTP ${response.status}`)
          return
        }

        const reader = response.body?.getReader()
        if (!reader) {
          callbacks.onError?.('No response body')
          return
        }

        const decoder = new TextDecoder()
        let buffer = ''
        let currentEvent = ''

        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() ?? ''

          for (const line of lines) {
            if (line.startsWith('event:')) {
              currentEvent = line.slice(6).trim()
            } else if (line.startsWith('data:')) {
              const dataStr = line.slice(5).trim()
              if (!dataStr || dataStr === '{}') { currentEvent = ''; continue }
              try {
                const data = JSON.parse(dataStr)
                if (currentEvent === 'stage') {
                  callbacks.onStage?.(data as KaStageEvent)
                } else if (currentEvent === 'file_done') {
                  callbacks.onFileDone?.(data as KaFileDoneEvent)
                } else if (currentEvent === 'summary') {
                  callbacks.onSummary?.(data as KaSummaryEvent)
                }
              } catch { /* malformed JSON — skip */ }
              currentEvent = ''
            }
          }
        }

        callbacks.onDone?.()
      } catch (err: unknown) {
        if ((err as any)?.name === 'AbortError') return
        callbacks.onError?.((err as any)?.message ?? 'SSE connection failed')
      }
    })()

  return () => controller.abort()
}

/**
 * Recursively extracts leaf file asset_ids from a ZIP asset's tree structure.
 *
 * Primary format (actual backend):
 *   { name, type: "file"|"folder", asset_id?, children?: [...] }
 *   — type:"file" nodes contribute their asset_id
 *   — type:"folder" nodes are recursed into via children
 *
 * Legacy formats also supported:
 *   - Array of asset_id strings
 *   - Array of { asset_id, ... } objects
 *   - Dict mapping filename → asset_id string
 *   - Dict mapping filename → { asset_id, ... }
 */
export function extractChildAssetIds(tree: Record<string, any> | Array<any>): string[] {
  if (!tree) return []
  const ids: string[] = []

  // Named-node format: the node has a "type" field that is "file" or "folder"
  if (!Array.isArray(tree) && typeof (tree as any).type === 'string') {
    const node = tree as any
    if (node.type === 'file' && node.asset_id) {
      return [node.asset_id as string]
    }
    if (node.type === 'folder' && Array.isArray(node.children)) {
      for (const child of node.children) {
        ids.push(...extractChildAssetIds(child))
      }
    }
    return ids
  }

  if (Array.isArray(tree)) {
    for (const item of tree) {
      if (typeof item === 'string') {
        ids.push(item)
      } else if (item && typeof item === 'object') {
        if (item.type === 'file' && item.asset_id) ids.push(item.asset_id)
        else if (item.type === 'folder' && item.children) ids.push(...extractChildAssetIds(item.children))
        else if (item.asset_id) ids.push(item.asset_id)
        else if (item.children) ids.push(...extractChildAssetIds(item.children))
        else ids.push(...extractChildAssetIds(item))
      }
    }
  } else {
    // Legacy dict: key = filename, value = asset_id string or nested object
    for (const val of Object.values(tree)) {
      if (typeof val === 'string') {
        ids.push(val)
      } else if (val && typeof val === 'object') {
        if ((val as any).asset_id) ids.push((val as any).asset_id)
        else ids.push(...extractChildAssetIds(val as Record<string, any>))
      }
    }
  }

  return [...new Set(ids)]
}

export interface UpcAsset {
  asset_id: string
  project_name: string
  phase: string   // "pseudocode" | "source_pseudocode" | "target_code" | "test_cases" | "testcases"
  parent_asset_id?: string
  [key: string]: unknown
}

/** Phase priority — higher index = further along in pipeline */
const PHASE_PRIORITY = [
  'pseudocode',
  'source_pseudocode',
  'target_code',
  'test_cases',
  'testcases',
]

/**
 * Given UPC assets for a project, returns a map of assetId → highest phase reached.
 * The UPC asset_id matches the knowledge asset_id directly (no phase suffix in the ID).
 * We also index by parent_asset_id when present to handle any format variations.
 */
export function buildAssetPhaseMap(
  upcAssets: UpcAsset[]
): Record<string, string> {
  const phaseMap: Record<string, string[]> = {}

  upcAssets.forEach((item) => {
    if (!item.phase) return
    // Index by asset_id and parent_asset_id (both point to the same base asset)
    const keys = new Set<string>()
    if (item.asset_id) keys.add(item.asset_id)
    if (item.parent_asset_id) keys.add(item.parent_asset_id as string)
    keys.forEach((key) => {
      if (!phaseMap[key]) phaseMap[key] = []
      phaseMap[key].push(item.phase)
    })
  })

  const result: Record<string, string> = {}
  Object.entries(phaseMap).forEach(([base, phases]) => {
    let highest = phases[0]
    let highestPriority = PHASE_PRIORITY.indexOf(phases[0])
    phases.forEach((p) => {
      const pri = PHASE_PRIORITY.indexOf(p)
      if (pri > highestPriority) { highestPriority = pri; highest = p }
    })
    result[base] = highest
  })

  return result
}

// ─── DB Migration ─────────────────────────────────────────────────────────────

/**
 * Pre-fill connection fields for the given database type.
 * GET /awgment-llm-local/get_db_connection_details?db_type={dbType}
 * Response: { status: "success", data: { host, port, username } }
 */
export const getDbConnectionDetails = async (dbType: string) => {
  try {
    const response = await axios.get(`${BASE}${PREFIX}/get_db_connection_details`, {
      params: { db_type: dbType },
      headers: authHeaders(),
    })
    if (response?.data?.status === 'success') {
      return { success: true, data: response.data }
    }
    return { success: false, message: response?.data?.message ?? 'Failed to fetch details' }
  } catch (error: unknown) {
    return {
      success: false,
      message:
        (error as any)?.response?.data?.message ??
        (error as any)?.message ??
        'Failed to fetch connection details',
    }
  }
}

/**
 * Connect to a database and fetch its databases/tables.
 * POST /awgment-llm-local/upload — multipart with DB credentials, no file.
 * Response data: { dbName: string[] } — keys are database names, values are table arrays.
 */
export const connectDatabase = async (params: {
  projectName: string
  dbType: string
  username: string
  host: string
  port: string
  password: string
  servicename?: string
  role?: string
  warehouse?: string
}): Promise<{ success: boolean; data?: Record<string, string[]>; message?: string }> => {
  try {
    const formData = new FormData()
    formData.append('project_name', params.projectName)
    formData.append('asset_type', 'DATABASE_MIGRATION')
    formData.append('operation', '')
    formData.append('db_type', params.dbType)
    formData.append('username', params.username)
    formData.append('host', params.host)
    formData.append('port', params.port)
    formData.append('password', params.password)
    if (params.servicename) formData.append('servicename', params.servicename)
    if (params.role) formData.append('role', params.role)
    if (params.warehouse) formData.append('warehouse', params.warehouse)

    const response = await axios.post(`${BASE}${PREFIX}/upload`, formData, {
      headers: authHeaders(),
    })
    return { success: true, data: response?.data?.data, message: response?.data?.message }
  } catch (error: unknown) {
    return {
      success: false,
      message:
        (error as any)?.response?.data?.message ??
        (error as any)?.message ??
        'Failed to connect to database',
    }
  }
}

/**
 * Create a DB migration asset with selected tables.
 * POST /awgment-llm-local/generate-report — multipart form data.
 */
export type DbReportStageEvent = {
  table?: string
  stage?: string
  message?: string
  progress?: number
  aggregate_progress?: number
}

export function generateDbReport(
  params: {
    projectName: string
    dbType: string
    dbHost: string
    port: string
    username: string
    password: string
    dbName: string
    servicename?: string
    role?: string
    warehouse?: string
    tablesList: string[]
  },
  callbacks: {
    onStage?: (data: DbReportStageEvent) => void
    onDone?: () => void
    onError?: (err: string) => void
  },
): () => void {
  const controller = new AbortController()

  const formData = new FormData()
  formData.append('db_type', params.dbType)
  formData.append('asset_type', 'DATABASE_MIGRATION')
  formData.append('project_name', params.projectName)
  formData.append('db_host', params.dbHost)
  formData.append('port', params.port)
  formData.append('username', params.username)
  formData.append('password', params.password)
  formData.append('db_name', params.dbName)
  formData.append('servicename', params.servicename ?? '')
  formData.append('role', params.role ?? '')
  formData.append('warehouse', params.warehouse ?? '')
  formData.append('tables_list', params.tablesList.join(','))

  ;(async () => {
    try {
      const response = await fetch(`${BASE}${PREFIX}/generate-report`, {
        method: 'POST',
        headers: authHeaders(),
        body: formData,
        signal: controller.signal,
      })

      if (!response.ok) {
        const text = await response.text().catch(() => '')
        callbacks.onError?.(text || `HTTP ${response.status}`)
        return
      }

      const reader = response.body?.getReader()
      if (!reader) {
        callbacks.onError?.('No response body')
        return
      }

      const decoder = new TextDecoder()
      let buffer = ''
      let currentEvent = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() ?? ''

        for (const line of lines) {
          if (line.startsWith('event:')) {
            currentEvent = line.slice(6).trim()
          } else if (line.startsWith('data:')) {
            const dataStr = line.slice(5).trim()
            if (!dataStr || dataStr === '{}') { currentEvent = ''; continue }
            try {
              const data = JSON.parse(dataStr)
              if (currentEvent === 'stage') callbacks.onStage?.(data as DbReportStageEvent)
            } catch { /* malformed JSON — skip */ }
            currentEvent = ''
          }
        }
      }

      callbacks.onDone?.()
    } catch (err: unknown) {
      if ((err as any)?.name === 'AbortError') return
      callbacks.onError?.((err as any)?.message ?? 'SSE connection failed')
    }
  })()

  return () => controller.abort()
}

// ─── Blueprint (pseudocode) ───────────────────────────────────────────────────

/** Preview existing pseudocode for an asset — returns text/JSON content */
export const previewPseudocode = (assetId: string) =>
  apiGet(`${PREFIX}/preview_all_pseudocodes`, { asset_id: assetId })

/** Preview original asset as PDF blob (Discovery screen) */
export const previewAssetForExport = async (assetId: string) => {
  try {
    const response = await axios.get(`${BASE}${PREFIX}/preview`, {
      params: { asset_id: assetId },
      headers: authHeaders(),
      responseType: 'blob',
    })
    return { success: true, data: response.data as Blob }
  } catch (error: unknown) {
    return {
      success: false,
      message:
        (error as any)?.response?.data?.message ??
        (error as any)?.message ??
        'Failed to preview asset',
    }
  }
}

/** Download / view original asset content (Discovery screen — triggered by clicking asset name) */
export const downloadAsset = async (assetId: string) => {
  try {
    const response = await axios.get(`${BASE}${PREFIX}/download`, {
      params: { asset_id: assetId },
      headers: authHeaders(),
      responseType: 'text',
    })
    return { success: true, data: response.data as string }
  } catch (error: unknown) {
    return {
      success: false,
      message:
        (error as any)?.response?.data?.message ??
        (error as any)?.message ??
        'Failed to load asset content',
    }
  }
}

/** Download asset as a binary blob — used for DB migration PDF assets via /download */
export const downloadAssetBlob = async (assetId: string) => {
  try {
    const response = await axios.get(`${BASE}${PREFIX}/download`, {
      params: { asset_id: assetId },
      headers: authHeaders(),
      responseType: 'blob',
    })
    return { success: true, data: response.data as Blob }
  } catch (error: unknown) {
    return {
      success: false,
      message:
        (error as any)?.response?.data?.message ??
        (error as any)?.message ??
        'Failed to download asset',
    }
  }
}

/** Generate modified pseudocode via AI instruction */
export const generateModifiedPseudocode = async (
  query: string,
  currentPseudocode: string,
  assetId: string
) => {
  try {
    const formData = new FormData()
    formData.append('query', query)
    formData.append('current_pseudocode', currentPseudocode)
    formData.append('asset_id', assetId)
    const response = await axios.post(
      `${BASE}${PREFIX}/generate_source_target`,
      formData,
      { headers: authHeaders() }
    )
    return { success: true, data: response.data }
  } catch (error: unknown) {
    return {
      success: false,
      message:
        (error as any)?.response?.data?.message ??
        (error as any)?.message ??
        'Failed to generate modified pseudocode',
    }
  }
}

/** Save finalised pseudocode */
export const saveFinalPseudocode = async (assetId: string, finalPseudocode: string) => {
  try {
    const formData = new FormData()
    formData.append('asset_id', assetId)
    formData.append('final_pseudocode', finalPseudocode)
    const response = await axios.post(
      `${BASE}${PREFIX}/save_final_pseudocode`,
      formData,
      { headers: authHeaders() }
    )
    return { success: true, data: response.data }
  } catch (error: unknown) {
    return {
      success: false,
      message:
        (error as any)?.response?.data?.message ??
        (error as any)?.message ??
        'Failed to save pseudocode',
    }
  }
}

/** Delete a pseudocode / DBML asset record */
export const deletePseudocodeAsset = async (assetId: string) => {
  try {
    const formData = new FormData()
    formData.append('asset_id', assetId)
    const response = await axios.post(
      `${BASE}${PREFIX}/delete_ps_assets_record`,
      formData,
      { headers: authHeaders() }
    )
    return { success: true, data: response.data }
  } catch (error: unknown) {
    return {
      success: false,
      message:
        (error as any)?.response?.data?.message ??
        (error as any)?.message ??
        'Failed to delete asset',
    }
  }
}

// ─── Build (target code) ──────────────────────────────────────────────────────

/** Generate / modify target code from pseudocode via AI instruction */
export const generateModifiedTargetCode = async (
  userInstruction: string,
  sourcePseudocode: string,
  targetCodeType: string,
  assetId: string
) => {
  try {
    const formData = new FormData()
    formData.append('user_instruction', userInstruction)
    formData.append('source_pseudocode', sourcePseudocode)
    formData.append('target_code_type', targetCodeType)
    formData.append('asset_id', assetId)
    const response = await axios.post(
      `${BASE}${PREFIX}/generate_modified_target_code`,
      formData,
      { headers: authHeaders() }
    )
    return { success: true, data: response.data }
  } catch (error: unknown) {
    return {
      success: false,
      message:
        (error as any)?.response?.data?.message ??
        (error as any)?.message ??
        'Failed to generate target code',
    }
  }
}

/** Save finalised target code */
export const saveFinalTargetCode = async (
  assetId: string,
  finalTargetCode: string,
  targetCodeType: string
) => {
  try {
    const formData = new FormData()
    formData.append('asset_id', assetId)
    formData.append('final_target_code', finalTargetCode)
    formData.append('target_code_type', targetCodeType)
    const response = await axios.post(
      `${BASE}${PREFIX}/save_final_target_code`,
      formData,
      { headers: authHeaders() }
    )
    return { success: true, data: response.data }
  } catch (error: unknown) {
    return {
      success: false,
      message:
        (error as any)?.response?.data?.message ??
        (error as any)?.message ??
        'Failed to save target code',
    }
  }
}

// ─── Validation (test cases) ──────────────────────────────────────────────────

/** Generate test cases for a source file */
export const generateTestCases = async (
  userInstruction: string,
  sourceCode: string,
  assetId: string
) => {
  try {
    const formData = new FormData()
    formData.append('user_instruction', userInstruction)
    formData.append('source_code', sourceCode)
    formData.append('asset_id', assetId)
    const response = await axios.post(
      `${BASE}${PREFIX}/generate_test_cases`,
      formData,
      { headers: authHeaders() }
    )
    return { success: true, data: response.data }
  } catch (error: unknown) {
    return {
      success: false,
      message:
        (error as any)?.response?.data?.message ??
        (error as any)?.message ??
        'Failed to generate test cases',
    }
  }
}

/** Save finalised test cases code */
export const saveFinalTestCases = async (assetId: string, finalTestCasesCode: string) => {
  try {
    const formData = new FormData()
    formData.append('asset_id', assetId)
    formData.append('final_testcases_code', finalTestCasesCode)
    const response = await axios.post(
      `${BASE}${PREFIX}/save_final_testcases_code`,
      formData,
      { headers: authHeaders() }
    )
    return { success: true, data: response.data }
  } catch (error: unknown) {
    return {
      success: false,
      message:
        (error as any)?.response?.data?.message ??
        (error as any)?.message ??
        'Failed to save test cases',
    }
  }
}
